<template>
    <div class="pppp-file-block">

        <h2 class="pppp-h2">
            <DeleteButton deleteEventName="deleteFile" :deleteEventPayload="id" />
            Tiedosto {{ id + 1 }}: {{ typeName }}</h2>

        <div class="row">
            <div class="col-sm-4">
                <Loader v-if="uploading" />
                <FormFile 
                    v-if="! value.fileName && ! value.originalFileName"
                    @change="fileSelected($event)" 
                    :id="'file-'+id" 
                    :name="'file['+id+']'" 
                    :maxFileSize="maxFileSize"
                    :accept="accept" />
                <div v-if="value.fileName">
                    <small v-if="value.originalFileName">
                        Tiedosto: <a :href="filePath" class="text-white" target="_blank">{{ value.originalFileName }}</a>
                    </small>
                    <!-- Preview for supported image files -->
                    <a v-if="isImg(value.fileName)" :href="filePath" target="_blank">
                        <img :src="filePath" class="img-preview" :alt="value.originalFileName" />
                    </a>
                    <!-- Audio player for audio files -->
                    <audio v-if="isPlayableAudio(value.fileName)" :src="filePath" style="width:100%;" controls></audio>
                    <!-- Video player for video files -->
                    <video v-if="isPlayableVideo(value.fileName)" :src="filePath" style="width:100%;" controls></video>
                </div>
            </div>
            <div class="col-sm-8">
                <FormInput 
                    v-model="value.description" 
                    :id="getId('place')" 
                    :required="true"
                    :label="getText('descriptionLabel')" 
                    :placeholder="getText('descriptionPlaceholder')" />
            </div>
        </div> <!-- /.row -->

        <div class="row">
            <div class="col-sm-4">
                <FormSelect 
                    v-if="type=='image'"
                    v-model="value.originalFormat"
                    :id="getId('originalFormat')" 
                    placeholder="Valitse"
                    :options="[
                        {text: 'Alun perin digitaalinen kuva', value: 'digital'},
                        {text: 'Skannattu paperi/filmioriginaalista', value: 'scanned'}
                    ]" 
                    :required="true"
                    label="Alkuperäisformaatti" />
            </div>
            <div class="col-sm-4">
                <FormInput v-model="value.time" :id="getId('time')" :label="getText('timeLabel')" />
            </div>
            <div class="col-sm-4">
                <FormInput v-model="value.place" :id="getId('place')" :label="getText('placeLabel')" />
            </div>
        </div> <!-- /.row -->

        <div class="row">
            <div class="col-sm-6 col-lg-4">
                <div class="pppp-validate-group" :class="{'pppp-validate-group-invalid': getInvalid('file-'+id+'-author')}">
                    <FormInput 
                        v-model="value.author" 
                        :disabled="Boolean(value.authorUnknown)"
                        @change="revalidate('file-'+id+'-author')"
                        @keyup="setauthorDependencies()"
                        :id="getId('author')" 
                        :name="getName('author')" 
                        :label="getText('authorLabel')" 
                        style="margin-bottom:0;" />
                    <FormCheckbox 
                        v-model="value.authorUnknown" 
                        :disabled="Boolean(value.author)"
                        @change="revalidate('file-'+id+'-author')"
                        :id="getId('authorUnknown')" 
                        :label="getText('authorUnknownLabel')" 
                        style="margin:0;" />
                    <div class="pppp-validation-msg">
                        <span class="glyphicon glyphicon-exclamation-sign"></span> Täytä vähintään yksi näistä kentistä!
                    </div>
                </div>

                <FormCheckbox 
                    v-model="value.authorPermission" 
                    :id="getId('authorPermission')" 
                    :label="getText('authorPermissionLabel')" 
                    style="margin-top:0;"/>

            </div>
            <div class="col-sm-6 col-lg-4">
                <FormInput 
                    v-model="value.authorInfo" 
                    :disabled="Boolean(! value.author)"
                    :id="getId('authorInfo')" 
                    :name="getName('authorInfo')" 
                    :label="getText('authorInfoLabel')" 
                    :help="true"
                    :helpId="'authorInfo-'+type" 
                    :helpTitle="getText('authorInfoLabel')" />
            </div>
            <div class="col-sm-6 col-lg-4">
                <FormInput 
                    v-model="value.otherCopyrightHolder" 
                    :id="getId('otherCopyrightHolder')" 
                    :label="getText('otherCopyrightHolderLabel')" 
                    :placeholder="getText('otherCopyrightHolderPlaceholder')" />
            </div>
        </div> <!-- /.row -->

        <div class="row">
            <div class="col-md-6">
                <FormTextArea 
                    v-model="value.addInfo" 
                    :id="getId('addInfo')" 
                    :label="getText('addInfoLabel')" />
            </div>
            <div class="col-md-6">
                <FormTextArea 
                    v-model="value.howIGotIt" 
                    :id="getId('howIGotIt')" 
                    :label="getText('howIGotItLabel')" 
                    :help="true"
                    :helpId="'howIGotIt-'+type" />
            </div>
        </div> <!-- /.row -->

    </div>
</template>

<script>
import DeleteButton from './DeleteButton.vue';
import FormCheckbox from './FormCheckbox.vue';
import FormFile from './FormFile.vue';
import FormInput from './FormInput.vue';
import FormSelect from './FormSelect.vue';
import FormTextArea from './FormTextArea.vue';
import Loader from './Loader.vue';

export default {
    name: 'FormFileData',
    components: {
        DeleteButton,
        FormCheckbox,
        FormFile,
        FormInput,
        FormSelect,
        FormTextArea,
        Loader
    },
    props: {
        type: {
            type: String,
            validator: function (value) {
                // Valid string values
                return ['image', 'video', 'audio'].indexOf(value) !== -1;
            }
        },
        id: Number,
        hash: String,
        maxFileSize: String,
        modelValue: Object,
        invalidGroups: Array
    },
    emits: ['update:modelValue'],
    computed: {
        value: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            }
        },
        typeName() {
            if (this.type == 'image') {
                return 'Kuva';
            } else if (this.type == 'video') {
                return 'Video';
            } else if (this.type == 'audio') {
                return 'Äänitallenne';
            }
            return null;
        },
        accept() {
            if (this.type == 'image') {
                return 'image/*,.pdf,.zip';
            } else if (this.type == 'video') {
                return 'video/*,.zip';
            } else if (this.type == 'audio') {
                return 'audio/*,.zip';
            }
            return null;
        },
        filePath() {
            if (this.value.fileName) {
                return process.env.VUE_APP_SERVER_PATH + 'wp-content/plugins/teatterimuseo-participation-project/api/file?hash=' + this.hash + '&fileType=' + this.type + '&fileName=' + this.value.fileName;
            }
            return null;
        }
    },
    watch: {
        'value.fileName': function(newVal) {
            if (newVal) {
                this.uploading = false;
            }
        }
    },
    data() {
        return {
            uploading: false,
            texts: {
                image: {
                    descriptionLabel: 'Kerro kuvasta muutamalla sanalla',
                    descriptionPlaceholder: 'Esim. keitä/mitä kuvassa on?',
                    timeLabel: 'Kuvausaika',
                    placeLabel: 'Kuvauspaikka',
                    authorLabel: 'Valokuvaajan nimi',
                    authorUnknownLabel: 'Valokuvaaja ei tiedossa',
                    authorPermissionLabel: 'Kuvaajalta kysytty lupa kuvan käyttöön',
                    authorInfoLabel: 'Valokuvaajan yhteystiedot',
                    otherCopyrightHolderLabel: 'Muu mahdollinen oikeuksienhaltija',
                    otherCopyrightHolderPlaceholder: null,
                    addInfoLabel: 'Muuta tietoa kuvasta',
                    howIGotItLabel: 'Miten kuva on päätynyt minulle'
                },
                video: {
                    descriptionLabel: 'Kuvaile tallenteen sisältöä muutamalla sanalla',
                    descriptionPlaceholder: null,
                    timeLabel: 'Tallennusaika',
                    placeLabel: 'Tallennuspaikka',
                    authorLabel: 'Kuvaajan nimi',
                    authorUnknownLabel: 'Kuvaaja ei tiedossa',
                    authorPermissionLabel: 'Käyttöoikeus kysytty organisaatiolta',
                    authorInfoLabel: 'Tallenteen tekijän/tekijöiden yhteystiedot',
                    otherCopyrightHolderLabel: 'Muut tallenteen tekijät',
                    otherCopyrightHolderPlaceholder: 'Esim. ohjaus, editointi, käsikirjoitus tms.',
                    addInfoLabel: 'Muuta tietoa tallenteesta',
                    howIGotItLabel: 'Miten tallenne on päätynyt minulle'
                },
                audio: {
                    descriptionLabel: 'Kuvaile äänitteen sisältöä muutamalla sanalla',
                    descriptionPlaceholder: null,
                    timeLabel: 'Äänitysaika',
                    placeLabel: 'Äänityspaikka',
                    authorLabel: 'Äänitteen tekijän nimi',
                    authorUnknownLabel: 'Tekijä ei tiedossa',
                    authorPermissionLabel: 'Käyttöoikeus kysytty äänitteen tekijältä/tekijöiltä',
                    authorInfoLabel: 'Äänitteen tekijän/tekijöiden yhteystiedot',
                    otherCopyrightHolderLabel: 'Muut äänitteen tekijät',
                    otherCopyrightHolderPlaceholder: 'Esim. leikkaus tai jos äänite on erillinen taiteellinen teos, sen tekijät',
                    addInfoLabel: 'Muuta tietoa äänitteestä',
                    howIGotItLabel: 'Miten äänite on päätynyt minulle'
                }
            }
        }
    },
    methods: {
        getId(str) {
            return 'file-' + this.id + '-' + str;
        },
        getName(str) {
            return 'file[' + this.id + '][' + str + ']';
        },
        getText(name) {
            let text = this.texts[this.type][name];
            if (text) {
                return text;
            }
            return null;
        },
        fileSelected(e) {
            console.log('file selected');
            console.log(e);
            this.uploading = true;
            this.$emitter.emit('uploadFile', {
                index: this.id, 
                fileType: this.type, 
                fileInput: e.target
            });
        },
        getInvalid(invalidId) {
            //console.log(this.invalidGroups);
            return this.invalidGroups.find(item => item == invalidId);
        },
        revalidate(invalidId) {
            if (this.getInvalid(invalidId)) {
                //console.log('Should revalidate');
                this.$emitter.emit('validateCustomFields');
            }
        },
        setauthorDependencies() {
            if (this.value.author) {
                // author is set so uncheck authorUnknown
                this.value.authorUnknown = null;
            } else {
                // No author so uncheck permission...
                this.value.authorPermission = null;
                // ...and clear author info
                this.value.authorInfo = null;
            }
        },
        isImg(fileName) {
            let imageFileExtensions = ['.jpg', '.jpeg', '.pjpg', '.png', '.gif'];
            if (imageFileExtensions.some(v => fileName.includes(v))) {
                return true;
            }
        },
        isPlayableAudio(fileName) {
            let audioFileExtensions = ['.mp3', '.wav', '.aac', '.ogg', '.webm'];
            if (audioFileExtensions.some(v => fileName.includes(v))) {
                return true;
            }
        },
        isPlayableVideo(fileName) {
            let videoFileExtensions = ['.mp4', '.ogg', '.ogv', '.webm'];
            if (videoFileExtensions.some(v => fileName.includes(v))) {
                return true;
            }
        }
    }
}
</script>

<style scoped>
.img-preview {
    max-height: 100px;
    width: auto;
}
</style>
